import { JSX } from "react";

import { cn } from "@lib/utils";

export type TColors = "text" | "secondary" | "error" | "subTitle" | "msg_time" | "grey-tertiary" | "primary";
export type TBreakpoints = "xs" | "sm" | "base" | "lg" | "xl";
export type TSizeText = keyof typeof sizeClasses;

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
	type?: "span" | "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
	size?: TSizeText;
	color?: TColors;
	padding?: "none" | "small" | "medium" | "large";
	weight?: "light" | "normal" | "bold" | "extraBold";
	align?: "left" | "center" | "right";
	children: React.ReactNode;
}

/** Size for text. Refer to tailwindcss https://tailwindcss.com/docs/font-size */
export const sizeClasses = {
	xs: "text-xs", // 12px
	sm: "text-sm", // 14px
	base: "text-base", // 16px
	lg: "text-lg", // 18px
	xl: "text-xl", // 20px
	"2xl": "text-2xl", // 24px
	"3xl": "text-3xl", // 28px
	"4xl": "text-4xl", // 36px
	"5xl": "text-5xl", // 48px
	"6xl": "text-6xl", // 60px
	"7xl": "text-7xl", // 72px
	"8xl": "text-8xl", // 96px
	"9xl": "text-9xl", // 128px
};

const colorClasses = {
	text: "text-white",
	secondary: "text-secondary",
	error: "text-error",
	subTitle: "text-sub-title",
	msg_time: "text-msg-time",
	"grey-tertiary": "text-grey-tertiary",
	primary: "text-primary",
};

const paddingClasses = {
	none: "p-0",
	small: "p-2",
	medium: "p-4",
	large: "p-6",
};

const weightClasses = {
	light: "font-light",
	normal: "font-normal",
	bold: "font-bold",
	extraBold: "font-black",
};

const alignClasses = {
	left: "text-left",
	center: "text-center",
	right: "text-right",
};

/** Styles for headings, paragraphs, lists...etc
 * @prop type - The type of the typography
 * @prop size - The size of the typography
 * @prop color - The color of the typography
 * @prop padding - The padding of the typography
 * @prop weight - The weight of the typography
 * @prop className - The class name to apply to the typography
 * @prop children - The content of the typography
 */
export const Typography = ({
	type = "p",
	size = "base",
	color = "text",
	padding = "none",
	weight = "normal",
	align = "left",
	className,
	children,
	...props
}: TypographyProps) => {
	const Component = type as keyof JSX.IntrinsicElements;

	return (
		// @ts-expect-error: TypeScript cannot differentiate between HTML and SVG elements here
		<Component
			className={cn(
				sizeClasses[size],
				colorClasses[color],
				paddingClasses[padding],
				weightClasses[weight],
				alignClasses[align],
				className,
			)}
			{...props}
		>
			{children}
		</Component>
	);
};

Typography.displayName = "Typography";
